/**
 * Analytics utilities for tracking events with Plausible
 *
 * Plausible automatically tracks page views.
 * Use these functions for custom events like form submissions and CTA clicks.
 */

// Extend Window interface for Plausible
declare global {
	interface Window {
		plausible?: (eventName: string, options?: { props?: Record<string, string | number | boolean> }) => void;
	}
}

const CONSENT_KEY = 'akonia_cookie_consent';

/**
 * Check if user has accepted analytics cookies
 */
export function hasAnalyticsConsent(): boolean {
	if (typeof window === 'undefined') return false;
	return localStorage.getItem(CONSENT_KEY) === 'accepted';
}

/**
 * Track a custom event in Plausible
 * Only fires if user has consented to analytics cookies
 * @param eventName - Name of the event (e.g., 'Newsletter Signup')
 * @param props - Optional additional properties
 */
export function trackEvent(
	eventName: string,
	props?: Record<string, string | number | boolean>
): void {
	if (!hasAnalyticsConsent()) return;
	if (typeof window !== 'undefined' && window.plausible) {
		window.plausible(eventName, props ? { props } : undefined);
	}
}

/**
 * Track newsletter subscription
 */
export function trackNewsletterSignup(source: string): void {
	trackEvent('Newsletter Signup', { source });
}

/**
 * Track contact form submission
 */
export function trackContactForm(inquiryType: string): void {
	trackEvent('Contact Form Submit', { inquiry_type: inquiryType });
}

/**
 * Track enrollment interest submission
 */
export function trackEnrollmentInterest(courseCount: number): void {
	trackEvent('Enrollment Interest', { course_count: courseCount });
}

/**
 * Track consultation request
 */
export function trackConsultationRequest(projectType: string): void {
	trackEvent('Consultation Request', { project_type: projectType });
}

/**
 * Track CTA button click
 */
export function trackCTAClick(ctaName: string, location: string): void {
	trackEvent('CTA Click', { cta_name: ctaName, location });
}

/**
 * Track discovery call booking (via Calendly)
 */
export function trackDiscoveryCallBooked(): void {
	trackEvent('Discovery Call Booked');
}

/**
 * Track blog post share
 */
export function trackBlogShare(platform: string, postSlug: string): void {
	trackEvent('Blog Share', { platform, post_slug: postSlug });
}

/**
 * Track outbound link click
 */
export function trackOutboundLink(url: string): void {
	trackEvent('Outbound Link', { url });
}

/**
 * Track AI Lab email capture (hero inline or gate)
 */
export function trackLabEmailSubmit(source: 'hero' | 'gate'): void {
	trackEvent('Lab Email Submit', { source });
}

/**
 * Track AI Lab module completion
 */
export function trackLabModuleComplete(slug: string): void {
	trackEvent('Lab Module Complete', { slug });
}

/**
 * Track AI Lab game completion
 */
export function trackLabGameComplete(slug: string): void {
	trackEvent('Lab Game Complete', { slug });
}

/**
 * Track an AI Lab soft masterclass CTA click
 */
export function trackLabCTAClick(location: string): void {
	trackEvent('Lab CTA Click', { cta_name: 'agentic-ai-masterclass', location });
}

/**
 * Track AI Lab level completion
 */
export function trackLabLevelComplete(slug: string, levelId: number, score: number): void {
	trackEvent('Lab Level Complete', { slug, level_id: levelId, score });
}

/**
 * Track AI Lab leaderboard score submission
 */
export function trackLabScoreSubmit(slug: string, score: number): void {
	trackEvent('Lab Score Submit', { slug, score });
}

/**
 * Track AI Lab social share
 */
export function trackLabShare(slug: string, platform: string): void {
	trackEvent('Lab Share', { slug, platform });
}
